import React, { useEffect, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useBettingStore, MatchOdds } from '../../state/bettingStore';
import { shortKey } from '../../utils/format';
import { toast } from 'sonner';

const TOKEN_DECIMALS = 9;

function formatTokenAmount(raw: string): string {
  const num = Number(raw) / 10 ** TOKEN_DECIMALS;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

interface Props {
  matchId: string;
  players: { wallet: string; username?: string }[];
}

export const SpectatorBetPanel: React.FC<Props> = ({ matchId, players }) => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();
  const [betAmount, setBetAmount] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);

  const { matchOdds, loading, fetchMatchOdds, placeBet } = useBettingStore();

  useEffect(() => {
    fetchMatchOdds(matchId);
    const interval = setInterval(() => fetchMatchOdds(matchId), 15000);
    return () => clearInterval(interval);
  }, [matchId, fetchMatchOdds]);

  // Calculate implied odds
  const totalPool = matchOdds.reduce((sum, o) => sum + Number(o.totalBet), 0);
  const getImpliedPct = (playerWallet: string) => {
    if (totalPool === 0) return 50;
    const playerTotal = matchOdds.find((o) => o.player === playerWallet);
    return playerTotal ? Math.round((Number(playerTotal.totalBet) / totalPool) * 100) : 0;
  };

  const handlePlaceBet = async () => {
    if (!wallet || !selectedPlayer || !betAmount) return;
    const amount = Number(betAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid token amount');
      return;
    }
    const rawAmount = BigInt(Math.round(amount * 10 ** TOKEN_DECIMALS)).toString();
    try {
      await placeBet(wallet, matchId, selectedPlayer, rawAmount);
      toast.success(`Bet placed on ${shortKey(selectedPlayer)}`);
      setBetAmount('');
      setSelectedPlayer(null);
      fetchMatchOdds(matchId);
    } catch {
      toast.error('Failed to place bet');
    }
  };

  return (
    <div className="arcade-panel rounded-xl p-4">
      <h3 className="mb-3 font-display text-sm uppercase tracking-wide text-arcade-pink">
        Spectator Betting
      </h3>

      {/* Odds Display */}
      <div className="mb-4 grid grid-cols-2 gap-3">
        {players.map((player) => {
          const pct = getImpliedPct(player.wallet);
          const odds = matchOdds.find((o) => o.player === player.wallet);
          const isSelected = selectedPlayer === player.wallet;

          return (
            <button
              key={player.wallet}
              onClick={() => setSelectedPlayer(player.wallet)}
              className={`rounded-lg border p-3 text-center transition ${
                isSelected
                  ? 'border-arcade-cyan bg-arcade-cyan/20'
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
            >
              <div className="font-arcade text-xs text-white">
                {player.username || shortKey(player.wallet)}
              </div>
              <div className="mt-1 font-display text-lg text-arcade-cyan">{pct}%</div>
              <div className="font-pixel text-[10px] text-white/40">
                {odds ? `${formatTokenAmount(odds.totalBet)} bet` : 'No bets'}
              </div>
            </button>
          );
        })}
      </div>

      {/* Bet Form */}
      {wallet ? (
        <div className="flex gap-2">
          <input
            type="number"
            value={betAmount}
            onChange={(e) => setBetAmount(e.target.value)}
            placeholder="Token amount"
            className="flex-1 rounded-lg border border-white/20 bg-black/50 px-3 py-2 font-arcade text-xs text-white placeholder:text-white/30 focus:border-arcade-pink focus:outline-none"
          />
          <button
            onClick={handlePlaceBet}
            disabled={loading || !selectedPlayer || !betAmount}
            className="rounded-lg border border-arcade-pink/50 bg-arcade-pink/20 px-4 py-2 font-pixel text-xs text-arcade-pink transition hover:bg-arcade-pink/30 disabled:opacity-50"
          >
            {loading ? 'Placing...' : 'Bet'}
          </button>
        </div>
      ) : (
        <div className="rounded-lg bg-white/5 p-3 text-center font-pixel text-xs text-white/50">
          Connect wallet to place bets
        </div>
      )}

      {/* Pool Info */}
      {totalPool > 0 && (
        <div className="mt-3 flex justify-between font-pixel text-[10px] text-white/40">
          <span>Total Pool: {formatTokenAmount(totalPool.toString())}</span>
          <span>3% house fee</span>
        </div>
      )}
    </div>
  );
};
